import { useEffect, useState } from "react";
import axios from "axios";
import L from "leaflet";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { API, useAuth } from "../App";
import { MapPin, Plus, X, Clock } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABEL = {
  rete_olistica: "Rete Olistica",
  ecovillaggio: "Ecovillaggio",
  permacultura: "Permacultura",
  collettivo: "Collettivo",
  progetto: "Progetto",
  comunita_vicinato: "Comunità di Vicinato",
  associazione: "Associazione",
  cooperativa: "Cooperativa",
  rete_cooperativa: "Rete Cooperativa",
};

const CATEGORY_COLOR = {
  rete_olistica: "#C05746",
  ecovillaggio: "#2D4A22",
  permacultura: "#79845B",
  collettivo: "#8b5a3c",
  progetto: "#525B54",
  comunita_vicinato: "#a85b3a",
  associazione: "#6b6f3f",
  cooperativa: "#3f6b6f",
  rete_cooperativa: "#5a4a8b",
};

const makeIcon = (color, dim = false) => L.divIcon({
  className: "tc-marker",
  html: `<div style="width:22px;height:22px;border-radius:50%;background:${color};border:3px solid #F9F6F0;box-shadow:0 2px 8px rgba(0,0,0,0.25);opacity:${dim ? 0.5 : 1}"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

export default function Realities() {
  const [items, setItems] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cat, setCat] = useState("");
  const [region, setRegion] = useState("");
  const [showForm, setShowForm] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    const params = {};
    if (cat) params.category = cat;
    if (region) params.region = region;
    const r = await axios.get(`${API}/realities`, { params });
    setItems(r.data);
  };

  const loadRegions = async () => {
    const r = await axios.get(`${API}/realities/regions`);
    setRegions(r.data);
  };

  useEffect(() => { load(); }, [cat, region]);
  useEffect(() => { loadRegions(); }, []);

  const myPending = items.filter(r => r.status === "pending");

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="realities-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-tc-secondary mb-3">Sul territorio</div>
          <h1 className="font-serif text-5xl md:text-6xl text-tc-primary">Le realtà del cambiamento</h1>
          <p className="text-tc-muted mt-4 max-w-xl">
            Una mappa viva di ecovillaggi, reti olistiche, comunità di vicinato e progetti rigenerativi.
            Sei una struttura? Iscriviti e apparirai sulla mappa dopo la verifica.
          </p>
        </div>
        {user && (
          <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center gap-2 self-start" data-testid="add-reality-btn">
            <Plus className="w-4 h-4" /> Iscrivi la tua realtà
          </button>
        )}
      </div>

      {myPending.length > 0 && (
        <div className="bg-tc-secondary/10 border border-tc-secondary/30 rounded-2xl p-4 mb-8 text-sm text-tc-secondary flex items-center gap-2" data-testid="pending-banner">
          <Clock className="w-4 h-4" /> Hai {myPending.length} realtà in attesa di approvazione
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4" data-testid="reality-filters">
        <FilterChip active={!cat} onClick={() => setCat("")} label="Tutte" />
        {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
          <FilterChip key={k} active={cat === k} onClick={() => setCat(k)} label={v} />
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="region-filters">
        <RegionChip active={!region} onClick={() => setRegion("")} label="Tutta Italia" count={regions.reduce((a, r) => a + r.count, 0)} />
        {regions.map(r => (
          <RegionChip key={r.region} active={region === r.region} onClick={() => setRegion(r.region)} label={r.region} count={r.count} />
        ))}
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-7 h-[600px] rounded-3xl overflow-hidden border border-tc" data-testid="reality-map">
          <MapContainer center={[42.5, 12.5]} zoom={6} style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {items.map(r => (
              <Marker key={r.id} position={[r.lat, r.lng]} icon={makeIcon(CATEGORY_COLOR[r.category] || "#2D4A22", r.status === "pending")}>
                <Popup>
                  <div className="font-serif text-lg text-tc-primary">{r.name}</div>
                  <div className="text-xs text-tc-muted uppercase tracking-wider">{CATEGORY_LABEL[r.category] || r.category} · {r.city}</div>
                  {r.status === "pending" && <div className="text-xs italic text-tc-secondary">In attesa</div>}
                  <p className="text-sm mt-2">{r.description?.slice(0, 120)}{r.description?.length > 120 ? '…' : ''}</p>
                  <Link to={`/realta/${r.id}`} className="text-xs text-tc-secondary underline mt-2 inline-block">Vedi profilo →</Link>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div className="md:col-span-5 h-[600px] overflow-y-auto pr-2 space-y-4" data-testid="reality-list">
          {items.length === 0 && <p className="text-tc-muted italic">Nessuna realtà in questa categoria.</p>}
          {items.map(r => (
            <Link to={`/realta/${r.id}`} key={r.id} className={`block bg-tc-surface rounded-2xl p-6 border hover:-translate-y-0.5 transition-transform ${r.status === 'pending' ? 'border-tc-secondary/40 opacity-80' : 'border-tc/50 hover:border-tc-primary/40'}`} data-testid={`reality-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-tc-secondary">
                  <span className="w-2 h-2 rounded-full" style={{ background: CATEGORY_COLOR[r.category] || "#2D4A22" }} />
                  {CATEGORY_LABEL[r.category] || r.category}
                </div>
                {r.status === "pending" && <span className="text-[10px] uppercase tracking-widest text-tc-secondary inline-flex items-center gap-1"><Clock className="w-3 h-3" /> In attesa</span>}
              </div>
              <h3 className="font-serif text-2xl text-tc-primary">{r.name}</h3>
              <div className="flex items-center gap-1 text-xs text-tc-muted mt-1"><MapPin className="w-3 h-3" /> {r.city} · {r.region}</div>
              <p className="mt-3 text-sm text-tc-muted leading-relaxed line-clamp-3">{r.description}</p>
            </Link>
          ))}
        </div>
      </div>

      {showForm && <AddRealityModal onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </main>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button onClick={onClick} data-testid={`filter-${label}`}
      className={`px-5 py-2 rounded-full text-sm transition-colors border ${active ? 'bg-tc-primary text-tc-bg border-tc-primary' : 'bg-transparent text-tc-muted border-tc hover:border-tc-primary'}`}>
      {label}
    </button>
  );
}

function RegionChip({ active, onClick, label, count }) {
  return (
    <button onClick={onClick} data-testid={`region-${label}`}
      className={`px-4 py-1.5 rounded-full text-xs transition-colors border inline-flex items-center gap-2 ${active ? 'bg-tc-secondary text-tc-bg border-tc-secondary' : 'text-tc-muted border-tc/60 hover:border-tc-secondary'}`}>
      {label} <span className={`text-[10px] px-1.5 rounded-full ${active ? 'bg-tc-bg/20' : 'bg-tc-bg'}`}>{count}</span>
    </button>
  );
}

function AddRealityModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", description: "", category: "rete_olistica",
    city: "", region: "", address: "", website: "", contact: ""
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/realities`, { ...form, lat: null, lng: null });
      if (r.data._claimed) {
        toast.success(`Esiste già "${r.data.name}". L'abbiamo collegata a te invece di creare un doppione.`);
      } else {
        toast.success("Realtà inviata. Sarà visibile dopo l'approvazione.");
      }
      onSaved();
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-tc-primary/40 backdrop-blur-sm z-40 flex items-center justify-center p-4" data-testid="add-reality-modal">
      <div className="bg-tc-bg rounded-3xl max-w-xl w-full p-8 border border-tc relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-tc-muted hover:text-tc-primary" data-testid="close-modal"><X /></button>
        <h2 className="font-serif text-3xl text-tc-primary mb-2">Iscrivi la tua realtà</h2>
        <p className="text-sm text-tc-muted mb-6">L'indirizzo viene geolocalizzato automaticamente. Sarà pubblica dopo l'approvazione.</p>
        <form onSubmit={submit} className="space-y-4">
          <Input label="Nome" value={form.name} onChange={v => setForm({ ...form, name: v })} required testid="r-name" />
          <TextArea label="Descrizione" value={form.description} onChange={v => setForm({ ...form, description: v })} required testid="r-desc" />
          <div>
            <label className="text-xs uppercase tracking-widest text-tc-muted">Categoria</label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary" data-testid="r-category">
              {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <Input label="Indirizzo (via, civico)" value={form.address} onChange={v => setForm({ ...form, address: v })} testid="r-address" placeholder="Es. Via Roma 12" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Città" value={form.city} onChange={v => setForm({ ...form, city: v })} required testid="r-city" />
            <Input label="Regione" value={form.region} onChange={v => setForm({ ...form, region: v })} required testid="r-region" />
          </div>
          <Input label="Sito web (opzionale)" value={form.website} onChange={v => setForm({ ...form, website: v })} testid="r-web" />
          <Input label="Email contatto (opzionale)" value={form.contact} onChange={v => setForm({ ...form, contact: v })} testid="r-contact" />
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="r-submit">
            {busy ? "Invio in corso…" : "Invia per approvazione"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testid, placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-tc-muted">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder}
        className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary" data-testid={testid} />
    </div>
  );
}

function TextArea({ label, value, onChange, required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-tc-muted">{label}</label>
      <textarea value={value} onChange={e => onChange(e.target.value)} required={required} rows={3}
        className="w-full bg-transparent border-b border-tc py-3 outline-none focus:border-tc-primary resize-none" data-testid={testid} />
    </div>
  );
}
